import { useRouter } from 'next/router'
import { type FunctionComponent, useCallback, useState } from 'react'

import { createFetcher } from '../../../libs/next/api/jsonApiHandler'
import type { API } from '../../../pages/api/forms/[handler]'
import { hasNoLetters } from '../../const/letterHelper'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import { InfoModal } from './InfoModal'
import style from './SuggestionModal.module.sass'

export type SuggestionModalProps = {
	handleClose: () => void
	isModalOpen: boolean
}

const api = createFetcher<API>('/api/forms/[handler]')

export const SuggestionModal: FunctionComponent<SuggestionModalProps> = ({ handleClose, isModalOpen }) => {
	const translate = useTranslate()
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
	const [isAlertVisible, setIsAlertVisible] = useState(false)
	const [message, setMessage] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)

	const router = useRouter()

	const closeModal = useCallback(() => {
		handleClose()
		setMessage('')
		setIsAlertVisible(false)
	}, [handleClose])

	const handleSubmit = async () => {
		if (message && message.length > 10 && !hasNoLetters(message)) {
			setIsLoading(true)
			try {
				await api('createBugReport', {
					message: String(message),
					url: router.asPath,
				})
				await api('sendMailReport', {
					message: String(message),
					url: router.asPath,
				})
				closeModal()
				setIsConfirmModalOpen(true)
				setMessage('')
				setIsLoading(false)
			} catch (err) {
				console.log('error', err)
			}
		} else {
			setIsAlertVisible(true)
		}
	}

	const closeConfirmModal = useCallback(() => setIsConfirmModalOpen(false), [])

	return (
		<>
			<InfoModal
				title={translate('suggestionModal.title')}
				lead={translate('suggestionModal.subtitle')}
				imageUrl="/images/micvsiti.png"
				buttonLabel={isLoading ? <div className={style.loader} /> : translate('suggestionModal.button.send')}
				isOpen={isModalOpen}
				buttonType="submit"
				imageAlternativeText={translate('suggestionModal.alt.image')}
				onClick={handleSubmit}
				disabled={!message || message.length < 10}
				closeModal={closeModal}
				content={
					<div className={style.modalContent}>
						<div className={style.inputWrapper}>
							<textarea
								className={style.inputTextArea}
								placeholder={translate('suggestionModal.textarea.placeholder')}
								rows={6}
								value={message || undefined}
								onChange={(e) => setMessage(e?.target.value)}
								onFocus={() => setIsAlertVisible(false)}
							/>
							{isAlertVisible && (
								<span className={style.inputAlert}>
									{hasNoLetters(message) ? (
										<Translation tr="message.punctuation.alert" />
									) : (
										<Translation tr="suggestionModal.alert" />
									)}
								</span>
							)}
						</div>
					</div>
				}
			/>
			<InfoModal
				title={translate('foundProblemBanner.modal.title')}
				lead={translate('foundProblemBanner.modal.subtitle')}
				imageUrl="/images/pes_modal_7.jpg"
				isOpen={isConfirmModalOpen}
				buttonLabel={translate('foundProblemBanner.modal.button.close')}
				buttonType="button"
				imageAlternativeText={translate('foundProblemBanner.alt.image')}
				onClick={closeConfirmModal}
				closeModal={closeConfirmModal}
			/>
		</>
	)
}
